/**
 * Broadpeak Streaming Session Module
 * Responsible for creating and managing SmartLib streaming sessions.
 *
 * @author Ajithkumar Kasi
 */

import { createError, createLogger, Error, ErrorCodes, Logger, LoggerLevel } from '@quickplay/foundation';

import { BroadpeakAdsSessionData } from '../BroadpeakAdsSession';
import { BroadpeakLoggerLevel, BroadpeakSessionInitializationData } from '../model/BroadpeakSessionInitializationData';
import { BroadpeakErrorCodes } from './BroadpeakErrorCodes';

import { SmartLib, LoggerManager } from '@broadpeak/smartlib';
import '@broadpeak/smartlib-analytics';
import '@broadpeak/smartlib-ad';
import { ShakaPlayerAdapter } from '@broadpeak/smartlib-shaka';
import { OMSDKHandler } from '@broadpeak/smartlib-omsdk';
import shaka from 'shaka-player';
import { PALConfiguration } from '@quickplay/pal';
import { AdViewState } from '../BroadpeakAdsPlayer';

declare const goog: any;
declare const AdManager: any;

const DEFAULT_ANALYTICS_ADDRESS = '';
const DEFAULT_NANO_CDN_HOST = '';
const DEFAULT_BROADPEAK_DOMAIN_NAMES = '*';
const DEFAULT_BROADPEAK_LOGGER_LEVEL = BroadpeakLoggerLevel.OFF;

const TAG_NAME = 'FL_BROADPEAK_SESSION';

let isSmartLibInitialized = false;
let isShakaLibraryAttached = false;
let smartLibInstance: any;

// =========================
// SMARTLIB INITIALIZATION
// =========================
function initializeSmartLib(initializationData: BroadpeakSessionInitializationData, logger: Logger): void {
  if (!SmartLib) {
    throw createError(
      BroadpeakErrorCodes.SMARTLIB_INSTANCE_NOT_INITIALIZED | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      'SmartLib is not available',
    );
  }

  smartLibInstance = SmartLib.getInstance();

  if (!isSmartLibInitialized) {
    smartLibInstance.init(
      initializationData.analyticsAddress ?? DEFAULT_ANALYTICS_ADDRESS,
      initializationData.nanoCDNHost ?? DEFAULT_NANO_CDN_HOST,
      initializationData.broadpeakDomainNames ?? DEFAULT_BROADPEAK_DOMAIN_NAMES,
    );

    // Validate initialization if SmartLib does not throw on failure.
    if (!smartLibInstance.internalAdManager?.initialized) {
      throw createError(
        BroadpeakErrorCodes.SMARTLIB_INSTANCE_NOT_INITIALIZED | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
        'SmartLib initialization failed',
      );
    }

    if (LoggerManager) {
      LoggerManager.getInstance().setLogLevel(initializationData.loggerLevel ?? DEFAULT_BROADPEAK_LOGGER_LEVEL);
    }

    isSmartLibInitialized = true;
    logger.log('SmartLib initialized');
  }

  applyGlobalConfigurations(initializationData);
}

function applyGlobalConfigurations(initializationData: BroadpeakSessionInitializationData): void {
  const { uuid, userAgent, deviceType, nanoCDNResolvingRetryDelay } = initializationData;

  if (uuid) {
    smartLibInstance.setUUID(uuid);
  }

  smartLibInstance.setUserAgent(userAgent ?? navigator.userAgent);

  if (deviceType) {
    smartLibInstance.setDeviceType(deviceType);
  }

  if (nanoCDNResolvingRetryDelay !== undefined) {
    smartLibInstance.setNanoCDNResolvingRetryDelay(nanoCDNResolvingRetryDelay);
  }
}

// =========================
// PAL CONFIGURATION
// =========================
function setupPAL(palConfiguration: PALConfiguration | undefined, allowStorage: boolean, logger: Logger): void {
  if (AdManager === undefined) {
    logger.log('Skipping PAL setup: AdManager is not present');
    return;
  }

  const adManagerInstance = AdManager.getInstance();

  if (palConfiguration) {
    if (goog === undefined || !goog?.pal) {
      logger?.warn('PAL SDK is not available. Skipping PAL setup.');
      return;
    }
    adManagerInstance.attachPALSDK(goog);

    const consentSettings = new goog.pal.ConsentSettings();
    consentSettings.allowStorage = allowStorage ?? false;

    adManagerInstance.setConsentSettings(consentSettings);

    adManagerInstance.setPalParameters(
      palConfiguration.descriptionUrl,
      palConfiguration.omidPartnerName,
      palConfiguration.omidPartnerVersion,
      palConfiguration.omidVersion,
      palConfiguration.playerType,
      palConfiguration.playerVersion,
      palConfiguration.ppid,
      palConfiguration.videoHeight ?? 0,
      palConfiguration.videoWidth ?? 0,
      palConfiguration.adWillAutoPlay ?? false,
      palConfiguration.adWillPlayMuted ?? false,
    );

    logger.log('PAL configured');
  }
}

// =========================
// SHAKA PLAYER SETUP
// =========================
function attachShakaLibrary(): void {
  if (isShakaLibraryAttached || !ShakaPlayerAdapter) {
    return;
  }

  ShakaPlayerAdapter.attachLibrary(shaka);
  isShakaLibraryAttached = true;
}

async function createAndAttachShakaPlayer(
  videoElement: HTMLVideoElement,
  internalSession: any,
): Promise<InstanceType<typeof shaka.Player>> {
  const shakaInstance = new shaka.Player();

  await shakaInstance.attach(videoElement);

  internalSession?.attachPlayer?.(shakaInstance);

  return shakaInstance;
}

// =========================
// STREAMING SESSION
// =========================
function createStreamingSession(initializationData: BroadpeakSessionInitializationData, logger: Logger): any {
  // Session options are intentionally omitted. The current Broadpeak SmartLib
  // (v6.0.4) does not expose StreamingSessionOptions. Revisit if the SDK adds
  // official support in a future release.

  const internalSession = smartLibInstance.createStreamingSession();

  applySessionParameters(internalSession, initializationData, logger);

  return internalSession;
}

function applySessionParameters(
  internalSession: any,
  initializationData: BroadpeakSessionInitializationData,
  logger: Logger,
): void {
  if (!internalSession) return;

  const { customParams, adParams } = initializationData;

  // 1. CUSTOM PARAMS (flexible)
  Object.entries(customParams ?? {}).forEach(([key, value]) => {
    logger.log(`Custom parameter ${key}=${value}`);
    internalSession.setCustomParameter?.(key, value);
  });

  // 2. AD PARAMS (critical timing for getURL)
  Object.entries(adParams ?? {}).forEach(([key, value]) => {
    logger.log(`Ad parameter ${key}=${value}`);
    internalSession.setAdParameter?.(key, value);
  });

  logger.log('Session parameters applied (custom + ad)');
}

// =========================
// URL RESOLUTION
// =========================
async function resolveBroadpeakUrl(internalSession: any, initializationUrl: string, logger: Logger): Promise<string> {
  if (!initializationUrl) {
    throw createError(
      BroadpeakErrorCodes.INVALID_INITIALIZATION_URL | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      'Initialization URL is missing or empty',
    );
  }

  const result = await internalSession?.getURL(initializationUrl);

  if (result?.isError?.()) {
    throw createError(
      BroadpeakErrorCodes.GET_URL_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      'GetURL returned an error result',
    );
  }

  const url = result?.getURL?.();

  logger.log('Resolved URL', url);

  return url;
}

// =========================
// PUBLIC API
// =========================

/**
 * Creates a Broadpeak streaming session using a single async flow:
 *
 * 1. Initializes SmartLib with analytics and CDN configuration
 * 2. Attaches OMSDK (if available)
 * 3. Creates a streaming session with custom/ad parameters
 * 4. Sets up PAL and attaches Shaka library
 * 5. Creates and attaches a Shaka Player to the video element
 * 6. Activates advertising on the session
 * 7. Sets ad view state
 * 8. Resolves the ad-stitched content URL via SmartLib `getURL()`
 *
 * On any failure, falls back to the provided `fallbackUrl`.
 */
export async function createBroadpeakSessionInstance(
  initializationData: BroadpeakSessionInitializationData,
  initializationUrl: string,
  fallbackUrl: string,
  videoElement: HTMLVideoElement,
  allowStorage?: boolean,
  palConfiguration?: PALConfiguration,
  logger?: Logger,
): Promise<BroadpeakAdsSessionData> {
  let internalSession: any;
  let shakaInstance: InstanceType<typeof shaka.Player> | undefined;
  let resolvedBroadpeakUrl: string | undefined;
  let internalIsFallback = false;
  let internalFallbackReason: Error | undefined;

  const sessionLogger = logger ?? createLogger(LoggerLevel.OFF, TAG_NAME);
  const resolvedAllowStorage = allowStorage ?? false;

  function setFallback(error: Error): void {
    internalIsFallback = true;
    internalFallbackReason = error;
    resolvedBroadpeakUrl = fallbackUrl;
  }

  async function cleanupSession(): Promise<void> {
    try {
      internalSession?.stopStreamingSession?.();
      internalSession?.detachPlayer?.();
      await internalSession?.destroy?.();
      sessionLogger.log('Session cleaned up');
    } catch (error) {
      sessionLogger.error('Failed to clean up session', error);
    } finally {
      internalSession = undefined;
      resolvedBroadpeakUrl = undefined;
    }
  }

  // Step 1: Initialize SmartLib
  try {
    initializeSmartLib(initializationData, sessionLogger);
  } catch (error) {
    setFallback(error as Error);
  }

  if (!internalIsFallback && smartLibInstance) {
    // Step 2: Attach OMSDK (if available)
    const verificationSettings = {
      contentUrl: globalThis.location?.href,
      videoElement,
      accessMode: 'full',
    };

    if ((globalThis as any).OmidSessionClient) {
      sessionLogger.log('Attaching OMSDK library');
      OMSDKHandler?.attachLibrary?.((globalThis as any).OmidSessionClient, verificationSettings);
    }

    // Step 3: Create streaming session with parameters
    internalSession = createStreamingSession(initializationData, sessionLogger);

    // Step 4: Setup PAL and attach Shaka library
    setupPAL(palConfiguration, resolvedAllowStorage, sessionLogger);
    attachShakaLibrary();

    // Step 5–8: Create player → activate ads → set view state → resolve URL
    try {
      internalSession?.setAdDataListener?.({
        onAdData: (adList: any[]) => {
          sessionLogger.log(' Received ad data with start positions:', adList);
        },
      });
      shakaInstance = await createAndAttachShakaPlayer(videoElement, internalSession);
      internalSession?.activateAdvertising();
      internalSession?.setAdViewState?.(AdViewState.NORMAL);

      resolvedBroadpeakUrl = await resolveBroadpeakUrl(internalSession, initializationUrl, sessionLogger);
    } catch (error) {
      await cleanupSession();
      setFallback(error as Error);
    }
  } else {
    sessionLogger.warn('SmartLib not available — session will not be created');
  }

  const session: BroadpeakAdsSessionData = {
    initializationData,

    get session(): any {
      return internalSession;
    },

    get broadpeakAdUrl(): string | undefined {
      return resolvedBroadpeakUrl;
    },

    get shakaPlayer(): any {
      return shakaInstance;
    },

    get isFallback(): boolean {
      return internalIsFallback;
    },

    get fallbackReason(): Error | undefined {
      return internalFallbackReason;
    },

    async destroy(): Promise<void> {
      await cleanupSession();
    },
  };

  return session;
}
